/**
 * Fetch wrapper that forwards Supabase/PostgREST non-2xx responses to PostHog.
 *
 * The Supabase client reports PostgREST errors via `{ data, error }` — it does
 * NOT throw. Callers often log `error` but rarely forward it to PostHog. This
 * wrapper captures the error at the transport layer so 403 (auth/RLS denial),
 * 404 (missing resource), 406 (.single() with no/many rows), and 5xx responses
 * are always reported.
 *
 * Filters:
 *   - Only captures non-2xx responses from Supabase hosts (to avoid capturing
 *     unrelated third-party fetches routed through the same global fetch).
 *   - Skips 401 and 429 (expected: auth expiry handled by SDK; rate limit by SDK).
 *   - Skips expected auth/business responses covered by isBenignSupabaseHttpError.
 */

import { SpanStatusCode } from "@opentelemetry/api"

import { isBenignSupabaseHttpError } from "@/shared/lib/monitoring/is-benign-server-exception"
import { tracedSupabaseFetch } from "@/shared/lib/monitoring/tracing"

type CaptureFn = (
  err: unknown,
  context: { scope: string; extra?: Record<string, unknown> }
) => void

const SKIP_STATUSES = new Set<number>([401, 429])
const AUTH_NETWORK_ERROR_MESSAGE =
  "Unable to reach the authentication service. Please try again."
const SUPABASE_NETWORK_ERROR_MESSAGE =
  "Unable to reach the service. Please try again."

function isSupabaseUrl(url: string): boolean {
  return url.includes(".supabase.co") || url.includes(".supabase.in") || url.includes("127.0.0.1:54321")
}

function resolveUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input
  if (input instanceof URL) return input.toString()
  return input.url
}

function resolveMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method.toUpperCase()
  if (input instanceof Request) return input.method.toUpperCase()
  return "GET"
}

function extractPathAndResource(rawUrl: string): {
  route_path: string | undefined
  resource: string | undefined
} {
  try {
    const u = new URL(rawUrl)
    const route_path = u.pathname
    let resource: string | undefined
    const restMatch = route_path.match(/^\/rest\/v1\/([^/?]+)/)
    if (restMatch) resource = restMatch[1]
    const authMatch = route_path.match(/^\/auth\/v1\/([^/?]+)/)
    if (authMatch) resource = `auth:${authMatch[1]}`
    const funcMatch = route_path.match(/^\/functions\/v1\/([^/?]+)/)
    if (funcMatch) resource = `fn:${funcMatch[1]}`
    const storageMatch = route_path.match(/^\/storage\/v1\/([^/?]+)/)
    if (storageMatch) resource = `storage:${storageMatch[1]}`
    return { route_path, resource }
  } catch {
    return { route_path: undefined, resource: undefined }
  }
}

function getNetworkErrorMessage(resource: string | undefined): string {
  return resource?.startsWith("auth:")
    ? AUTH_NETWORK_ERROR_MESSAGE
    : SUPABASE_NETWORK_ERROR_MESSAGE
}

async function peekError(response: Response): Promise<{
  message?: string
  code?: string
  details?: string
}> {
  try {
    const clone = response.clone()
    const text = await clone.text()
    if (!text) return {}
    try {
      const body = JSON.parse(text)
      if (body && typeof body === "object") {
        const b = body as Record<string, unknown>
        const message = [b.message, b.error, b.error_description, b.msg].find(
          (value): value is string => typeof value === "string"
        )
        return {
          message,
          code: typeof b.code === "string" ? b.code : undefined,
          details: typeof b.details === "string" ? b.details : undefined,
        }
      }
    } catch {
      // not JSON — fall through to raw text (truncated)
    }
    return { message: text.slice(0, 500) }
  } catch {
    return {}
  }
}

function isJsonContentType(contentType: string | null): boolean {
  if (!contentType) return false
  const lower = contentType.toLowerCase()
  return lower.includes("json")
}

function extractCauseCode(cause: unknown): string | undefined {
  if (!cause) return undefined

  if (cause instanceof AggregateError) {
    for (const error of cause.errors) {
      const nestedCode = extractCauseCode(error)
      if (nestedCode) return nestedCode
    }
    return cause.name
  }

  if (cause instanceof Error) {
    const code = (cause as { code?: unknown }).code
    return typeof code === "string" && code ? code : cause.name
  }

  if (typeof cause === "object") {
    const record = cause as {
      code?: unknown
      errors?: unknown
      name?: unknown
    }
    if (typeof record.code === "string" && record.code) return record.code
    if (typeof record.name === "string" && record.name) return record.name
    if (Array.isArray(record.errors)) {
      for (const error of record.errors) {
        const nestedCode = extractCauseCode(error)
        if (nestedCode) return nestedCode
      }
    }
  }

  return undefined
}

/**
 * Wrap the global `fetch` so that non-2xx responses from Supabase hosts are
 * reported to PostHog via `capture`. The capture call is fire-and-forget and
 * never affects the response returned to the caller.
 */
export function createReportingFetch(
  capture: CaptureFn,
  scope: string
): typeof fetch {
  return async function reportingFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    const url = resolveUrl(input)
    const method = resolveMethod(input, init)
    const { route_path, resource } = extractPathAndResource(url)
    const operationName = resource ?? route_path ?? "request"

    return tracedSupabaseFetch(
      operationName,
      {
        "http.method": method,
        "http.route": route_path ?? "unknown",
        scope,
      },
      async (span) => {
        let response: Response
        try {
          response = await fetch(input, init)
        } catch (err) {
          if (!isSupabaseUrl(url)) {
            throw err
          }

          const message =
            err instanceof Error && err.message
              ? err.message
              : "Supabase request failed"

          span.setAttribute("http.status_code", 503)
          span.setStatus({ code: SpanStatusCode.ERROR, message })

          // Transient transport-layer failures (undici "fetch failed" when a
          // Supabase call blips) are fully handled here — we degrade to the
          // synthetic 503 below. Capturing them as exceptions is pure noise:
          // the worktree path baked into the stack frames mints a separate
          // error-tracking issue per dev worktree and call site (PostHog
          // 019f0659). Downgrade to a structured log (PostHog Logs, not error
          // tracking) with safe metadata only — never raw request/response
          // content — and keep the 503 fallback intact.
          const causeCode =
            err instanceof Error ? extractCauseCode(err.cause) : undefined
          console.warn("[supabase] transport fetch failed — degraded to 503", {
            scope,
            resource,
            route_path,
            http_method: method,
            status_code: 503,
            error: err instanceof Error ? err.name : "UnknownError",
            cause: causeCode,
          })

          return new Response(
            JSON.stringify({
              message: getNetworkErrorMessage(resource),
              code: "supabase_network_error",
              details: message,
              hint: null,
            }),
            {
              status: 503,
              statusText: "Service Unavailable",
              headers: { "content-type": "application/json" },
            }
          )
        }

        span.setAttribute("http.status_code", response.status)
        if (response.ok) {
          span.setStatus({ code: SpanStatusCode.OK })
          return response
        }

        if (!isSupabaseUrl(url)) {
          span.setStatus({
            code: SpanStatusCode.ERROR,
            message: `HTTP ${response.status}`,
          })
          return response
        }
        if (SKIP_STATUSES.has(response.status)) {
          return response
        }

        const { message: responseMessage, code, details } = await peekError(response)
        const message = responseMessage ?? response.statusText ?? `HTTP ${response.status}`
        if (
          isBenignSupabaseHttpError(
            response.status,
            message,
            resource,
            code,
            details
          )
        ) {
          return response
        }

        span.setStatus({ code: SpanStatusCode.ERROR, message })
        const contentType = response.headers.get("content-type")
        const nonJsonBody = !isJsonContentType(contentType)

        const err = new Error(
          `Supabase ${method} ${resource ?? route_path ?? "unknown"} → ${response.status}: ${message}`
        )
        err.name = `SupabaseHttp${response.status}`

        try {
          capture(err, {
            scope,
            extra: {
              status_code: response.status,
              http_method: method,
              route_path,
              resource,
              content_type: contentType ?? undefined,
              non_json_body: nonJsonBody || undefined,
            },
          })
        } catch {
          // never let capture failure propagate
        }

        // When an upstream proxy (Cloudflare, gateway, login redirect) replaces the
        // Supabase response with HTML, the SDK's downstream `response.json()` call
        // throws SyntaxError("Unexpected token '<'…"). Substitute a JSON-bodied
        // response so the SDK surfaces a structured `{ data: null, error }` instead
        // of crashing the React Query queryFn.
        if (nonJsonBody) {
          return new Response(
            JSON.stringify({
              message,
              code: `non_json_response_${response.status}`,
              details: contentType
                ? `Server returned ${contentType} (status ${response.status}) instead of JSON`
                : `Server returned status ${response.status} with non-JSON body`,
              hint: null,
            }),
            {
              status: response.status,
              statusText: response.statusText,
              headers: { "content-type": "application/json" },
            }
          )
        }

        return response
      }
    )
  }
}
